/**
 * Default avatar library for Neighborli.
 * Mix of AI-generated photos and consistent SVG illustrations.
 * 12 diverse options covering various skin tones, hair styles, and cultural/religious dress.
 */

import { avatarWomanLight, avatarWomanHijab, avatarManDark } from '../assets';

function svgToDataUrl(svg) {
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
}

function makeAvatar({ bg = '#c8dfc8', skin, hair, hairStyle, shirt, accessory = null, label }) {
  const hairPaths = {
    // long straight hair hanging down sides
    longStraight: `
      <path d="M 34 48 Q 30 75 33 90 L 40 88 Q 38 68 40 52 Z" fill="${hair}"/>
      <path d="M 66 48 Q 70 75 67 90 L 60 88 Q 62 68 60 52 Z" fill="${hair}"/>
      <ellipse cx="50" cy="40" rx="18" ry="15" fill="${hair}"/>`,
    // short natural afro
    afro: `
      <circle cx="50" cy="36" r="22" fill="${hair}"/>`,
    // hijab — simple wrap
    hijab: `
      <ellipse cx="50" cy="43" rx="22" ry="20" fill="${hair}"/>
      <ellipse cx="50" cy="53" rx="28" ry="14" fill="${hair}"/>
      <path d="M 28 54 Q 30 80 34 88 L 40 87 Q 36 72 36 54 Z" fill="${hair}"/>
      <path d="M 72 54 Q 70 80 66 88 L 60 87 Q 64 72 64 54 Z" fill="${hair}"/>`,
    // short men's cut
    shortCut: `
      <path d="M 32 48 Q 31 36 50 32 Q 69 36 68 48 Q 62 38 50 37 Q 38 38 32 48 Z" fill="${hair}"/>`,
    // curly / wavy
    curly: `
      <path d="M 32 50 Q 28 30 50 28 Q 72 30 68 50 Q 62 35 50 35 Q 38 35 32 50 Z" fill="${hair}"/>
      <circle cx="33" cy="44" r="5" fill="${hair}"/>
      <circle cx="67" cy="44" r="5" fill="${hair}"/>
      <circle cx="42" cy="30" r="6" fill="${hair}"/>
      <circle cx="58" cy="30" r="6" fill="${hair}"/>`,
    // bun / updo
    bun: `
      <path d="M 33 50 Q 32 38 50 34 Q 68 38 67 50 Q 61 40 50 40 Q 39 40 33 50 Z" fill="${hair}"/>
      <circle cx="50" cy="30" r="8" fill="${hair}"/>`,
    // turban
    turban: `
      <path d="M 50 22 Q 72 22 74 42 Q 64 34 50 34 Q 36 34 26 42 Q 28 22 50 22 Z" fill="${hair}"/>
      <ellipse cx="50" cy="34" rx="22" ry="10" fill="${hair}"/>
      <ellipse cx="50" cy="38" rx="24" ry="8" fill="${hair}" opacity="0.7"/>`,
    // kufi cap
    kufi: `
      <path d="M 32 50 Q 30 38 50 36 Q 70 38 68 50 Q 62 42 50 42 Q 38 42 32 50 Z" fill="${hair}"/>
      <path d="M 32 50 Q 36 44 50 44 Q 64 44 68 50 Q 58 46 50 46 Q 42 46 32 50 Z" fill="${hair}" opacity="0.4"/>`,
  };

  const hp = hairPaths[hairStyle] || hairPaths.shortCut;

  // Optional earrings
  const earrings = accessory === 'earrings'
    ? `<circle cx="32" cy="66" r="2.5" fill="#d4af37"/><circle cx="68" cy="66" r="2.5" fill="#d4af37"/>`
    : '';

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <circle cx="50" cy="50" r="50" fill="${bg}"/>
  <!-- shirt/body -->
  <path d="M 20 100 Q 22 78 50 76 Q 78 78 80 100 Z" fill="${shirt}"/>
  <!-- neck -->
  <rect x="45" y="66" width="10" height="12" rx="4" fill="${skin}"/>
  <!-- hair behind head -->
  ${hp}
  <!-- head -->
  <ellipse cx="50" cy="58" rx="17" ry="19" fill="${skin}"/>
  <!-- eyebrows -->
  <path d="M 42 51 Q 45 49 48 51" stroke="#4a3728" stroke-width="1.2" fill="none" stroke-linecap="round"/>
  <path d="M 52 51 Q 55 49 58 51" stroke="#4a3728" stroke-width="1.2" fill="none" stroke-linecap="round"/>
  <!-- eyes -->
  <ellipse cx="45" cy="56" rx="3" ry="2.8" fill="#fff"/>
  <ellipse cx="55" cy="56" rx="3" ry="2.8" fill="#fff"/>
  <circle cx="45.5" cy="56.5" r="2" fill="#2a1a0a"/>
  <circle cx="55.5" cy="56.5" r="2" fill="#2a1a0a"/>
  <circle cx="46.2" cy="55.8" r="0.7" fill="white"/>
  <circle cx="56.2" cy="55.8" r="0.7" fill="white"/>
  <!-- nose -->
  <path d="M 49 59 Q 50 62 51 59" stroke="${skin === '#FDDBB4' ? '#d4956a' : skin === '#F0C8A0' ? '#c87832' : '#7a4010'}" stroke-width="1" fill="none" stroke-linecap="round"/>
  <!-- smile -->
  <path d="M 44 64 Q 50 70 56 64" stroke="${skin === '#FDDBB4' ? '#c07840' : '#8B4513'}" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  ${earrings}
</svg>`;

  return { label, svg: svgToDataUrl(svg) };
}

const SVG_AVATARS = [
  makeAvatar({
    label: 'Woman, light skin, long brown hair',
    skin: '#FDDBB4', hair: '#5C3317', hairStyle: 'longStraight',
    shirt: '#7097C0', accessory: 'earrings',
  }),
  makeAvatar({
    label: 'Woman, medium skin, curly hair',
    skin: '#D4956A', hair: '#2D1B0E', hairStyle: 'curly',
    shirt: '#C08060', accessory: 'earrings',
  }),
  makeAvatar({
    label: 'Woman, dark skin, natural afro',
    skin: '#8B4513', hair: '#1a0a00', hairStyle: 'afro',
    shirt: '#9B59B6', accessory: 'earrings',
  }),
  makeAvatar({
    label: 'Woman, medium skin, hijab',
    skin: '#C68642', hair: '#2E6B8A', hairStyle: 'hijab',
    shirt: '#2E6B8A', bg: '#c8dfc8',
  }),
  makeAvatar({
    label: 'Woman, light skin, bun',
    skin: '#F5CBA7', hair: '#C19A6B', hairStyle: 'bun',
    shirt: '#E67E73', accessory: 'earrings',
  }),
  makeAvatar({
    label: 'Woman, olive skin, long black hair',
    skin: '#C9956C', hair: '#1a1a1a', hairStyle: 'longStraight',
    shirt: '#3A7D5A', accessory: 'earrings',
  }),
  makeAvatar({
    label: 'Man, dark skin, short hair',
    skin: '#4A2512', hair: '#1a1a1a', hairStyle: 'shortCut',
    shirt: '#2C5F8A',
  }),
  makeAvatar({
    label: 'Man, light skin, short brown hair',
    skin: '#FDDBB4', hair: '#6B3A2A', hairStyle: 'shortCut',
    shirt: '#4A7C59',
  }),
  makeAvatar({
    label: 'Man, medium brown skin, beard and short hair',
    skin: '#A0622A', hair: '#2D1B0E', hairStyle: 'shortCut',
    shirt: '#8B5E3C',
  }),
  makeAvatar({
    label: 'Man, medium skin, turban (Sikh)',
    skin: '#C68642', hair: '#1E5FA0', hairStyle: 'turban',
    shirt: '#1E5FA0',
  }),
  makeAvatar({
    label: 'Man, light olive skin, short black hair',
    skin: '#E8C49A', hair: '#1a1a1a', hairStyle: 'shortCut',
    shirt: '#5B7FA6',
  }),
  makeAvatar({
    label: 'Man, brown skin, kufi cap',
    skin: '#8B5E3C', hair: '#1a1a1a', hairStyle: 'kufi',
    shirt: '#2D5A3D',
  }),
];

// Mix in the 3 AI-generated photo avatars (bundled locally — no runtime dependency on Emergent CDN).
const AI_AVATARS = [
  {
    label: 'Woman, light skin, long hair (photo)',
    svg: avatarWomanLight,
  },
  {
    label: 'Woman, medium skin, hijab (photo)',
    svg: avatarWomanHijab,
  },
  {
    label: 'Man, dark skin, short hair (photo)',
    svg: avatarManDark,
  },
];

export const DEFAULT_AVATARS = [
  ...AI_AVATARS,
  ...SVG_AVATARS,
];
